
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useSocket } from './SocketContext';

const InternContext = createContext({});


function mapLeave(l) {
  const colors = {'Casual Leave':'#6366f1','Sick Leave':'#ef4444','Personal Leave':'#f59e0b','Comp Off':'#3b82f6','Earned Leave':'#2563eb','WFH':'#8b5cf6','Loss of Pay':'#dc2626'};
  return { type: l.leave_type||l.type||'', total: l.total||0, used: l.used||0, remaining: l.remaining||0, color: l.color||colors[l.leave_type||l.type]||'#6366f1' };
}

export function InternProvider({ children }) {
  const { connected, on, off } = useSocket();
  const [d, setD] = useState({
    loaded: false,
    internData: { name:"", id:"", email:"", role:"", team:"", manager:"", startDate:"", endDate:"", progress:0 },
    checklist: [], onboardingDocs: [],
    attendance: { totalDays:0, present:0, leaves:0, holidays:0 },
    leaveSummary: [], leaveReqs: [],
    notifications: [], chatMessages: [],
    internEmployees: [], initChats: [],
    profileDetails: [], uploadedDocs: [],
    dailyAttendance: {}, monthlyAttStats: {},
    initLeaveReqs: [], initPermReqs: [],
    calendarEvents: [],
    permBalance: null,
  });

  const fetchAll = useCallback(async () => {
    const mo = new Date().getMonth() + 1;
    const yr = new Date().getFullYear();
    const [prof, onb, att, leave, leaveReqs, notifs, docs, channels, calEv, perms, permBalance, dailyAtt, monthStats, meData] = await Promise.all([
      api.getProfile().catch(()=>({})),
      api.getOnboardingStatus().catch(()=>({})),
      api.getAttendanceSummary().catch(()=>({ totalDays:0,present:0,leaves:0,holidays:0 })),
      api.getLeaveBalance().catch(()=>[]),
      api.getMyLeaveRequests().catch(()=>[]),
      api.getNotifications().catch(()=>[]),
      api.getMyDocs().catch(()=>[]),
      api.getChannels().catch(()=>[]),
      api.getEvents(mo, yr).catch(()=>[]),
      api.getMyPermissions().catch(()=>[]),
      api.getPermissionBalance().catch(()=>null),
      api.getDailyAttendance(mo, yr).catch(()=>[]),
      api.getMonthStats(mo, yr).catch(()=>null),
      api.getMe().catch(()=>({})),
    ]);
      const profData = prof.profile || prof || {};
      const onbUser = onb.user || {};
      const onbProfile = onb.profile || (onb.user ? { full_name: onb.user.name } : {});
      const bank = onb.bank_details || {};
      const edu = Array.isArray(onb.education) ? onb.education : [];
      const mergedProfile = {
        ...profData, ...onbProfile, ...(bank && typeof bank === 'object' ? bank : {}),
        ...(meData && typeof meData === 'object' ? meData : {}),
        emp_id: onbUser.emp_id || profData.emp_id, name: onbProfile.full_name || onbUser.name || profData.name,
        ...(edu[0] ? { education: edu.map(e => `${e.degree || ''} ${e.institution || ''}`).join(', ') } : {}),
      };
      const allLeave = Array.isArray(leaveReqs) ? leaveReqs : [];
      const allPerms = Array.isArray(perms) ? perms : [];
      
      // Deduplicate checklist - one time only, no repeats
      const rawChecklist = onb.checklist || [];
      const seenLabels = new Set();
      const checklist = rawChecklist.filter(c => {
        if (seenLabels.has(c.label)) return false;
        seenLabels.add(c.label);
        return true;
      });
      
      // Build daily attendance map
      const dailyMap = {};
      (Array.isArray(dailyAtt) ? dailyAtt : []).forEach(r => {
        const dateKey = r.date ? r.date.substring(0, 10) : '';
        if (dateKey) dailyMap[dateKey] = { s: r.status, checkIn: r.check_in, checkOut: r.check_out };
      });
      
      const onboardingProgress = onb.progress != null ? onb.progress : (checklist.length ? Math.round((checklist.filter(c => c.done).length / checklist.length) * 100) : 0);
      
      setD(prev => ({
        ...prev, loaded: true,
        internData: { 
          name: mergedProfile.full_name || mergedProfile.name || "", 
          id: mergedProfile.emp_id || "", 
          email: mergedProfile.personal_email || mergedProfile.email || "", 
          role: mergedProfile.designation || "", 
          team: mergedProfile.team || "", 
          manager: mergedProfile.manager_name || mergedProfile.manager || "", 
          startDate: mergedProfile.joining_date || "", 
          endDate: mergedProfile.end_date || "", 
          progress: onboardingProgress 
        },
        checklist,
        onboardingDocs: (onb.documents || []).map(doc => ({ ...doc, id: doc.id, name: doc.doc_name || doc.name })),
        attendance: att || { totalDays:0, present:0, leaves:0, holidays:0 },
        leaveSummary: (Array.isArray(leave) ? leave : []).map(mapLeave),
        initLeaveReqs: allLeave.filter(r => r.request_type !== 'permission').map(r => ({
          ...r,
          type: r.leave_type || r.type || '',
          from: r.from_date || r.from || '',
          to: r.to_date || r.to || '',
          days: r.days || 0,
          reason: r.reason || '',
          status: r.status || 'pending',
          pmStatus: r.pm_status || '',
          hrStatus: r.hr_status || '',
          pmComment: r.pm_comment || '',
          hrComment: r.hr_comment || '',
        })),
        initPermReqs: allPerms.map(r => ({
          ...r,
          type: 'Permission',
          date: r.date || '',
          fromTime: r.from_time || '',
          toTime: r.to_time || '',
          hours: r.hours || 0,
          reason: r.reason || '',
          status: r.status || 'pending',
          pmStatus: r.pm_status || '',
          hrStatus: r.hr_status || '',
        })),
        notifications: notifs || [],
        profileDetails: (() => {
          const source = mergedProfile || {};
          const asDate = (v) => {
            if (!v) return '-';
            const d = new Date(v);
            return Number.isNaN(d.getTime())
              ? String(v)
              : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
          };
          const pick = (...keys) => {
            for (const k of keys) {
              const val = source[k];
              if (val !== undefined && val !== null && String(val).trim() !== '') return val;
            }
            return '';
          };
          return [
            { label: 'Date of Birth', value: String(asDate(pick('dob', 'date_of_birth'))) },
            { label: 'Blood Group', value: String(pick('blood_group', 'bloodGroup') || '-') },
            { label: 'Gender', value: String(pick('gender') || '-') },
            { label: 'Marital Status', value: String(pick('marital_status', 'maritalStatus') || '-') },
            { label: 'Phone', value: String(pick('personal_phone', 'phone', 'mobile') || '-') },
            { label: 'Personal Email', value: String(pick('personal_email', 'other_email', 'email') || '-') },
            { label: 'PAN Number', value: String(pick('pan_number', 'panNumber') || '-') },
            { label: 'Current Address', value: String(pick('current_address', 'currentAddress', 'address') || '-') },
            { label: 'Current City', value: String(pick('current_city', 'currentCity', 'city') || '-') },
            { label: 'Current State', value: String(pick('current_state', 'currentState', 'state') || '-') },
            { label: 'Current Pincode', value: String(pick('current_pincode', 'currentPincode', 'pincode') || '-') },
            { label: 'Permanent Address', value: String(pick('permanent_address', 'permanentAddress') || '-') },
            { label: 'Permanent City', value: String(pick('permanent_city', 'permanentCity') || '-') },
            { label: 'Permanent State', value: String(pick('permanent_state', 'permanentState') || '-') },
            { label: 'Permanent Pincode', value: String(pick('permanent_pincode', 'permanentPincode') || '-') },
            { label: 'Designation', value: String(pick('designation', 'role') || '-') },
            { label: 'Manager Id', value: String(pick('manager_id', 'managerId', 'manager_name', 'manager') || '-') },
            { label: 'Joining Date', value: String(pick('joining_date', 'joiningDate', 'expected_joining_date') || '-') },
            { label: 'End Date', value: String(pick('end_date', 'endDate') || '-') },
            { label: 'Location', value: String(pick('location') || '-') }
          ];
        })(),
        uploadedDocs: (Array.isArray(docs) ? docs : []).map(d=>({id:d.id, name:d.doc_name||d.name, date:d.uploaded_at||d.date, type:d.doc_type})),
        initChats: channels || [],
        calendarEvents: calEv || [],
        dailyAttendance: dailyMap,
        monthlyAttStats: monthStats ? { [String(mo).padStart(2, '0')]: monthStats } : prev.monthlyAttStats || {},
        permBalance: permBalance || null,
      }));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!connected) return;
    const handle = () => { fetchAll().catch(() => {}); };
    on('notification', handle);
    on('notification:new', handle);
    on('leave:update', handle);
    on('leave:new', handle);
    on('permission:update', handle);
    on('helpdesk:update', handle);
    on('calendar:event', handle);
    on('announcement:new', handle);
    return () => {
      off('notification', handle);
      off('notification:new', handle);
      off('leave:update', handle);
      off('leave:new', handle);
      off('permission:update', handle);
      off('helpdesk:update', handle);
      off('calendar:event', handle);
      off('announcement:new', handle);
    };
  }, [connected, on, off, fetchAll]);

  return <InternContext.Provider value={{ ...d, refreshInternData: fetchAll }}>{children}</InternContext.Provider>;
}

export const useInternData = () => useContext(InternContext);
